import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { CasinoCommandNames } from 'utils/constants/casino/casino-command-names';
import { CASINO_LANG_EXCEPTIONS } from 'utils/constants/languages/language-format';
import { ECasinoQueryKey } from 'utils/constants/store/query-keys';
import { SUCCESS_STATUS_CODE } from 'utils/constants/swarm/backend-status-codes';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { GLOBALS } from 'utils/generic/global-variables';
import { isMobile } from 'utils/is-mobile';
import { NewCasinoCategory } from 'interfaces/new-casino';
import {
  setCanRequestCategoriesNestedData,
  setCasinoOriginalCategories
} from 'store/actions/new-casino';

type TUseCategoriesFetchParams = {
  providerIdsForCategories?: string;
  setExternalLoading?: (isLoading: boolean) => void;
  shouldFetch?: boolean;
};

export const useCategoriesFetch = ({
  providerIdsForCategories,
  setExternalLoading,
  shouldFetch
}: TUseCategoriesFetchParams): void => {
  const dispatch = useDispatch();

  const { data } = useQuery({
    queryKey: [ECasinoQueryKey.CasinoCategories],
    queryFn: () => getCategories(),
    retry: false,
    enabled: !!shouldFetch
  });

  useEffect(() => {
    data && dispatch(setCasinoOriginalCategories(data));
  }, [data]);

  const getCategories = () => {
    setExternalLoading?.(true);

    return new Promise<NewCasinoCategory[]>(resolve => {
      if (SpringConfigs.MOCKED_DATA) {
        import(
          /* webpackChunkName: "casino-categories-mock" */ 'utils/mocks/casino/categories-mock'
        ).then(mockModule => {
          resolve(mockModule.CASINO_CATEGORIES_MOCK);
        });
      } else {
        const lang = document.documentElement.lang;

        const params = SpringConfigs?.DECENTRALIZED_CASINO_URL
          ? {
              partnerId: SpringConfigs.DECENTRALIZED_CASINO_PARTNER_ID
            }
          : {
              partner_id: SpringConfigs.PARTNER_ID,
              is_mobile: Number(isMobile()),
              only_categories: 1,
              country: SpringConfigs.CURRENT_COUNTRY_CODE,
              lang:
                GLOBALS.languageConfigs.casinoLangPrefix ||
                CASINO_LANG_EXCEPTIONS[lang] ||
                CASINO_LANG_EXCEPTIONS[lang.slice(0, 2)] ||
                SpringConfigs.LANGUAGE_PREFIX,
              ...(!!providerIdsForCategories && {
                providers: providerIdsForCategories
              })
            };

        const url = SpringConfigs?.DECENTRALIZED_CASINO_URL
          ? `${SpringConfigs.DECENTRALIZED_CASINO_URL}${CasinoCommandNames.GET_DECENTRALIZED_CATEGORIES}`
          : `${SpringConfigs.CASINO_URL}/${CasinoCommandNames.GET_OPTIONS}`;

        axios
          .get<{ categories: NewCasinoCategory[]; status: string }>(url, {
            params
          })
          .then((resp: any) => {
            if (
              resp.status === SUCCESS_STATUS_CODE &&
              resp.data &&
              (resp.data.status === 'ok' ||
                SpringConfigs?.DECENTRALIZED_CASINO_URL)
            ) {
              if (SpringConfigs?.DECENTRALIZED_CASINO_URL) {
                const result = resp.data.categories.map(
                  (category: NewCasinoCategory) => {
                    return {
                      id: category.categoryId,
                      name: category.name,
                      title: category.name,
                      games_count: category.gameCount,
                      badge: null
                    };
                  }
                );

                resolve(result);
              } else {
                resolve(resp.data.categories);
              }

              dispatch(setCanRequestCategoriesNestedData(true));
            }
          })
          .finally(() => {
            setExternalLoading?.(false);
          });
      }
    });
  };
};
